/**
 * Starting an order from a studio appointment.
 *
 * The Today page in superadmin-v2 has a "Start order" button on each
 * appointment. It opens this app with the booking's name, email and phone in
 * the URL's #fragment (never sent to a server, and removed from the address
 * bar straight away). Arriving that way is a new customer, so the previous
 * one's session is wiped first, exactly as "Start New Order" does. The details
 * then wait in sessionStorage until the checkout form uses them as its
 * starting values. Staff still check and can change every one, and the
 * garments are never filled in: a booking's description of them isn't
 * reliable.
 *
 * Kept in sessionStorage, not staff prefs: resetKioskSession clears it, so it
 * can't follow the customer into the next order.
 */
import { resetKioskSession } from "./kioskSession";

const KEY = "fxry-appointment-prefill";
const HASH_PARAM = "appointment";

const text = (value, max) =>
  typeof value === "string" ? value.trim().slice(0, max) : "";

/** Only the fields we fill, as short strings; null when there's nothing. */
export const sanitizePrefill = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const clean = {
    firstName: text(raw.firstName, 50),
    lastName: text(raw.lastName, 50),
    email: text(raw.email, 100),
    // "phone" in the link from v2, "number" (the form field) once stored.
    number: text(raw.phone ?? raw.number, 30),
    name: text(raw.name, 100),
    // Their answer on our booking page, in the checkout's own wording
    // (superadmin-v2 lib/startOrder.js hearAboutUsFrom). Checked against the
    // list where it is used.
    hearAboutUs: text(raw.hearAboutUs, 140),
  };
  return clean.firstName || clean.lastName || clean.email ? clean : null;
};

/** base64url(UTF-8 JSON) → object, or null. */
export const decodePrefill = (encoded) => {
  try {
    const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return sanitizePrefill(JSON.parse(new TextDecoder().decode(bytes)));
  } catch (error) {
    return null;
  }
};

/** Run once before the app renders. Returns true when an appointment arrived. */
export const takePrefillFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const encoded = params.get(HASH_PARAM);
    if (!encoded) return false;
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
    const prefill = decodePrefill(encoded);
    if (!prefill) return false;
    resetKioskSession();
    sessionStorage.setItem(KEY, JSON.stringify(prefill));
    return true;
  } catch (error) {
    console.error("[APPOINTMENT PREFILL] Could not read it:", error);
    return false;
  }
};

/** The waiting details, or null. */
export const loadPrefill = () => {
  try {
    return sanitizePrefill(JSON.parse(sessionStorage.getItem(KEY)));
  } catch (error) {
    return null;
  }
};
